export const PRAGUE_ZONE_SOURCE = 'Geoportál Praha';
export const PRAGUE_ZONE_ATTRIBUTION = 'datový podklad © IPR Praha';
export const PRAGUE_ZONE_ENDPOINT =
  '/arcgis/rest/services/Hosted/DOP_CUR_DOP_ZPS_PARKUSEKY_P/FeatureServer/0/query';

export type Position = [longitude: number, latitude: number];

export interface ParkingZoneProperties {
  objectid_1?: number;
  category?: string;
  code?: string;
  tariftext?: string;
  poskyt?: string;
}

export interface ParkingZoneFeature {
  type: 'Feature';
  id?: string | number;
  properties: ParkingZoneProperties;
  geometry:
    | { type: 'Polygon'; coordinates: Position[][] }
    | { type: 'MultiPolygon'; coordinates: Position[][][] };
}

export interface ParkingZoneCollection {
  type: 'FeatureCollection';
  features: ParkingZoneFeature[];
  properties?: { exceededTransferLimit?: boolean };
}

const METRES_PER_DEGREE = 111_320;

export function createPragueZoneUrl(
  latitude: number,
  longitude: number,
  radiusMetres = 850,
): string {
  const latitudeDelta = radiusMetres / METRES_PER_DEGREE;
  const longitudeScale = Math.cos((latitude * Math.PI) / 180);
  const longitudeDelta = radiusMetres / (METRES_PER_DEGREE * longitudeScale);
  const envelope = [
    longitude - longitudeDelta,
    latitude - latitudeDelta,
    longitude + longitudeDelta,
    latitude + latitudeDelta,
  ].join(',');

  const params = new URLSearchParams({
    where: '1=1',
    geometry: envelope,
    geometryType: 'esriGeometryEnvelope',
    inSR: '4326',
    spatialRel: 'esriSpatialRelIntersects',
    outFields: 'objectid_1,category,code,tariftext,poskyt',
    returnGeometry: 'true',
    outSR: '4326',
    geometryPrecision: '6',
    orderByFields: 'objectid_1 ASC',
    resultRecordCount: '2000',
    f: 'geojson',
  });
  return `${PRAGUE_ZONE_ENDPOINT}?${params.toString()}`;
}

export async function fetchPragueZones(
  latitude: number,
  longitude: number,
  signal?: AbortSignal,
): Promise<ParkingZoneCollection> {
  const response = await fetch(createPragueZoneUrl(latitude, longitude), {
    signal,
    headers: { Accept: 'application/json' },
  });
  if (!response.ok)
    throw new Error(`Geoportál Praha odpověděl HTTP ${response.status}.`);
  const collection = (await response.json()) as ParkingZoneCollection;
  if (
    collection.type !== 'FeatureCollection' ||
    !Array.isArray(collection.features)
  ) {
    throw new Error('Geoportál Praha vrátil neplatná zónová data.');
  }
  if (collection.properties?.exceededTransferLimit) {
    throw new Error('Výsledek zón překročil bezpečný limit.');
  }
  return {
    ...collection,
    features: collection.features.filter(
      (feature) =>
        feature.type === 'Feature' &&
        Boolean(feature.properties?.code?.trim()) &&
        (feature.geometry?.type === 'Polygon' ||
          feature.geometry?.type === 'MultiPolygon'),
    ),
  };
}

export function findZoneAtPoint(
  features: ParkingZoneFeature[],
  latitude: number,
  longitude: number,
): ParkingZoneFeature | null {
  return (
    features.find((feature) =>
      featureContainsPoint(feature, latitude, longitude),
    ) ?? null
  );
}

export function featureContainsPoint(
  feature: ParkingZoneFeature,
  latitude: number,
  longitude: number,
): boolean {
  const polygons =
    feature.geometry.type === 'Polygon'
      ? [feature.geometry.coordinates]
      : feature.geometry.coordinates;
  return polygons.some((polygon) => {
    const exterior = polygon[0];
    if (!exterior || !pointInRing(longitude, latitude, exterior)) return false;
    return !polygon
      .slice(1)
      .some((hole) => pointInRing(longitude, latitude, hole));
  });
}

function pointInRing(
  longitude: number,
  latitude: number,
  ring: Position[],
): boolean {
  let inside = false;
  for (
    let current = 0, previous = ring.length - 1;
    current < ring.length;
    previous = current++
  ) {
    const [currentLongitude, currentLatitude] = ring[current];
    const [previousLongitude, previousLatitude] = ring[previous];
    const intersects =
      currentLatitude > latitude !== previousLatitude > latitude &&
      longitude <
        ((previousLongitude - currentLongitude) *
          (latitude - currentLatitude)) /
          (previousLatitude - currentLatitude) +
          currentLongitude;
    if (intersects) inside = !inside;
  }
  return inside;
}
